<template>
  <div>
    <Title :pages="[{ icon: 'scale-balanced', page: page_title.toUpperCase() }]"></Title>

    <v-container fluid v-if="list_access">
      <!-- Selección de emisor + estado -->
      <v-card class="mb-4">
        <v-card-text>
          <div class="row align-center">
            <div class="col-md-5">
              <v-combobox
                v-model="emitter_selected"
                autocomplete="off"
                item-text="name"
                item-value="id"
                :items="emitters"
                label="Emisor"
                class="pt-0 mt-0"
                hide-details
                :loading="loading_emitters"
                return-object
                @change="loadMapeo"
              ></v-combobox>
            </div>
            <div class="col-md-7 d-flex justify-md-end align-center flex-wrap" v-if="mapeoData">
              <v-chip v-if="mapeoData.contabilidad_habilitada" color="success" dark small class="mr-3 my-1">
                <v-icon left small>check_circle</v-icon>
                Contabilidad habilitada
                <template v-if="mapeoData.fecha_inicio"> — desde {{ mapeoData.fecha_inicio }}</template>
              </v-chip>
              <v-chip v-else color="grey" dark small class="mr-3 my-1">
                <v-icon left small>remove_circle</v-icon> Contabilidad no habilitada
              </v-chip>

              <v-btn
                v-if="false"
                text
                small
                color="primary"
                class="my-1 mr-3"
                :loading="loading_sugerencias"
                @click="abrirAutoMacheo"
              >
                <v-icon left small>auto_fix_high</v-icon> Macheo automático
              </v-btn>

              <v-btn color="blue-grey" dark small class="my-1 mr-3" :loading="dialog_export" @click="exportFile">
                Exportar <font-awesome-icon class="ml-2" icon="download" />
              </v-btn>

              <v-btn
                v-if="!mapeoData.contabilidad_habilitada"
                color="success"
                small
                class="my-1"
                :disabled="!isComplete || !update_access"
                :loading="loading_action"
                @click="habilitar"
              >
                <v-icon left small>check_circle</v-icon> Habilitar
              </v-btn>
              <v-btn
                v-else
                color="error"
                outlined
                small
                class="my-1"
                :disabled="!update_access"
                :loading="loading_action"
                @click="deshabilitar"
              >
                <v-icon left small>remove_circle</v-icon> Deshabilitar
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <template v-if="mapeoData">
        <!-- Progreso global -->
        <v-card class="mb-4" :loading="loading_mapeo">
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="subtitle-1 font-weight-bold">Progreso del mapeo contable</span>
              <span class="subtitle-1 font-weight-bold" :class="isComplete ? 'green--text' : 'orange--text'">
                {{ totalMapeadas }} / {{ totalItems }}
              </span>
            </div>
            <v-progress-linear
              :value="progressPercent"
              height="10"
              rounded
              :color="isComplete ? 'green' : 'orange'"
            ></v-progress-linear>
            <div class="d-flex flex-wrap mt-3">
              <v-chip small label class="mr-2 mb-1" :color="groupComplete('money_accounts') ? 'green lighten-4' : 'orange lighten-4'">
                <v-icon x-small left>account_balance_wallet</v-icon>
                Cuentas de tesorería {{ mapeadas('money_accounts') }}/{{ total('money_accounts') }}
              </v-chip>
              <v-chip small label class="mr-2 mb-1" :color="groupComplete('conceptos') ? 'green lighten-4' : 'orange lighten-4'">
                <v-icon x-small left>sell</v-icon>
                Conceptos {{ mapeadas('conceptos') }}/{{ total('conceptos') }}
              </v-chip>
              <v-chip small label class="mr-2 mb-1" :color="groupComplete('defaults') ? 'green lighten-4' : 'orange lighten-4'">
                <v-icon x-small left>settings_suggest</v-icon>
                Cuentas de sistema {{ mapeadas('defaults') }}/{{ total('defaults') }}
              </v-chip>
            </div>
            <div v-if="isComplete && !mapeoData.contabilidad_habilitada" class="caption green--text mt-2">
              <v-icon x-small color="green">check_circle</v-icon>
              Todo mapeado. Ya podés habilitar la contabilidad de este emisor.
            </div>
          </v-card-text>
        </v-card>

        <!-- Tabs de mapeo -->
        <v-card>
          <v-tabs v-model="tab" grow show-arrows background-color="grey lighten-3">
            <v-tab>
              Cuentas de tesorería
              <v-chip x-small class="ml-2" :color="pendientes('money_accounts') ? 'red' : 'green'" dark>
                {{ pendientes('money_accounts') ? pendientes('money_accounts') + ' pend.' : 'OK' }}
              </v-chip>
            </v-tab>
            <v-tab>
              Conceptos
              <v-chip x-small class="ml-2" :color="pendientes('conceptos') ? 'red' : 'green'" dark>
                {{ pendientes('conceptos') ? pendientes('conceptos') + ' pend.' : 'OK' }}
              </v-chip>
            </v-tab>
            <v-tab>
              Cuentas de sistema
              <v-chip x-small class="ml-2" :color="pendientes('defaults') ? 'red' : 'green'" dark>
                {{ pendientes('defaults') ? pendientes('defaults') + ' pend.' : 'OK' }}
              </v-chip>
            </v-tab>
            <v-tab>
              Ret. y percepciones
              <v-chip x-small class="ml-2" color="blue-grey" dark>
                {{ regimenesMapeados }}/{{ regimenes.length }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <!-- ===== Cuentas de tesorería ===== -->
            <v-tab-item>
              <div class="pa-4">
                <v-text-field
                  v-model="searchMoney"
                  append-icon="search"
                  label="Buscar cuenta de tesorería"
                  single-line
                  hide-details
                  dense
                  clearable
                  class="mb-3"
                ></v-text-field>

                <div v-if="!filteredMoney.length" class="caption grey--text text-center py-6">Sin cuentas de tesorería.</div>

                <div v-for="m in filteredMoney" :key="'ma-' + m.id" class="mapeo-row">
                  <div class="mapeo-row__info">
                    <div class="font-weight-medium">{{ m.name }}</div>
                    <div class="caption grey--text">{{ m.code }}</div>
                  </div>
                  <div class="mapeo-row__select">
                    <v-autocomplete
                      v-model="m.account_id"
                      :items="m.is_resultado ? accountsResultado : accountsPatrimonial"
                      item-text="label"
                      item-value="id"
                      :label="m.is_resultado ? 'Cuenta de resultado' : 'Cuenta contable'"
                      dense
                      hide-details
                      :clearable="!mapeoData.contabilidad_habilitada"
                      :loading="m.saving"
                      :disabled="!update_access || m.saving"
                      @change="onChangeMoney(m)"
                    ></v-autocomplete>
                  </div>
                  <div class="mapeo-row__status">
                    <v-icon v-if="m.account_id && isMoneyCoherent(m)" color="green">check_circle</v-icon>
                    <v-tooltip v-else-if="m.account_id" bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="red">error</v-icon>
                      </template>
                      <span>Naturaleza incorrecta: {{ m.is_resultado ? "debe imputar a Ingresos/Egresos" : "debe imputar a una cuenta patrimonial" }}.</span>
                    </v-tooltip>
                    <v-icon v-else color="grey lighten-1">radio_button_unchecked</v-icon>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- ===== Conceptos ===== -->
            <v-tab-item>
              <div class="pa-4">
                <div class="caption grey--text mb-3">
                  Asigná la cuenta de <strong>Ingresos</strong> (cuando el concepto se vende) y/o la de <strong>Egresos</strong> (cuando se compra). Con al menos una alcanza.
                </div>
                <v-text-field
                  v-model="searchConcepto"
                  append-icon="search"
                  label="Buscar concepto"
                  single-line
                  hide-details
                  dense
                  clearable
                  class="mb-3"
                ></v-text-field>

                <div v-if="!filteredConceptos.length" class="caption grey--text text-center py-6">Sin conceptos.</div>

                <div v-for="c in filteredConceptos" :key="'co-' + c.id" class="mapeo-row mapeo-row--doble">
                  <div class="mapeo-row__info">
                    <div class="font-weight-medium">{{ c.name }}</div>
                  </div>
                  <div class="mapeo-row__select-double">
                    <div class="mapeo-col">
                      <v-autocomplete
                        v-if="showVenta(c)"
                        v-model="c.account_venta_id"
                        :items="accountsIngresos"
                        item-text="label"
                        item-value="id"
                        label="Venta (Ingresos)"
                        dense
                        hide-details
                        :clearable="!mapeoData.contabilidad_habilitada"
                        :loading="c.saving"
                        :disabled="!update_access || c.saving"
                        @change="onChangeConcepto(c)"
                      ></v-autocomplete>
                      <div v-else class="mapeo-na">No aplica</div>
                    </div>
                    <div class="mapeo-col">
                      <v-autocomplete
                        v-if="showCompra(c)"
                        v-model="c.account_compra_id"
                        :items="accountsEgresos"
                        item-text="label"
                        item-value="id"
                        label="Compra (Egresos)"
                        dense
                        hide-details
                        :clearable="!mapeoData.contabilidad_habilitada"
                        :loading="c.saving"
                        :disabled="!update_access || c.saving"
                        @change="onChangeConcepto(c)"
                      ></v-autocomplete>
                      <div v-else class="mapeo-na">No aplica</div>
                    </div>
                  </div>
                  <div class="mapeo-row__status">
                    <v-icon v-if="c.account_venta_id || c.account_compra_id" color="green">check_circle</v-icon>
                    <v-icon v-else color="grey lighten-1">radio_button_unchecked</v-icon>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- ===== Cuentas de sistema ===== -->
            <v-tab-item>
              <div class="pa-4">
                <div class="caption grey--text mb-3">
                  Cuentas fijas que el motor usa para armar los asientos (deudores, IVA, retenciones, etc.).
                </div>
                <div v-for="d in mapeoData.defaults" :key="'df-' + d.key" class="mapeo-row">
                  <div class="mapeo-row__info">
                    <div class="font-weight-medium d-flex align-center">
                      {{ defaultName(d.key) }}
                      <v-tooltip bottom max-width="320" v-if="defaultTip(d.key)">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small class="ml-1" color="grey">help_outline</v-icon>
                        </template>
                        <span>{{ defaultTip(d.key) }}</span>
                      </v-tooltip>
                    </div>
                    <div class="caption grey--text">Sugerido: {{ defaultHint(d.key) }}</div>
                  </div>
                  <div class="mapeo-row__select">
                    <v-autocomplete
                      v-model="d.account_id"
                      :items="accounts"
                      item-text="label"
                      item-value="id"
                      label="Cuenta contable"
                      dense
                      hide-details
                      :clearable="!mapeoData.contabilidad_habilitada"
                      :loading="d.saving"
                      :disabled="!update_access || d.saving"
                      @change="onChangeDefault(d)"
                    ></v-autocomplete>
                  </div>
                  <div class="mapeo-row__status">
                    <v-icon v-if="d.account_id" color="green">check_circle</v-icon>
                    <v-icon v-else color="grey lighten-1">radio_button_unchecked</v-icon>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- ===== Retenciones y percepciones por régimen ===== -->
            <v-tab-item>
              <div class="pa-4">
                <div class="caption grey--text mb-3">
                  Opcional. Sin mapeo, todas las retenciones y percepciones van a las cuentas de sistema
                  genéricas. Abrirlas por impuesto y jurisdicción permite conciliar el saldo de cada
                  cuenta contra el depósito de su régimen (SICORE, SIRE, ARBA, AGIP, SIRCREB…), que se
                  declara y se paga por separado.
                </div>

                <v-text-field
                  v-model="searchRegimen"
                  append-icon="search"
                  label="Buscar régimen o jurisdicción"
                  single-line
                  hide-details
                  dense
                  clearable
                  class="mb-3"
                ></v-text-field>

                <div v-if="!filteredRegimenes.length" class="caption grey--text text-center py-6">
                  No hay regímenes de retención ni percepción dados de alta.
                </div>

                <div v-for="r in filteredRegimenes" :key="'rg-' + regimenKey(r)" class="mapeo-row mapeo-row--doble">
                  <div class="mapeo-row__info">
                    <div class="font-weight-medium">{{ r.tributo_name }}</div>
                    <div class="caption grey--text">
                      {{ r.jurisdiccion ? r.jurisdiccion.name : "Nacional" }}
                    </div>
                  </div>
                  <div class="mapeo-row__select-double">
                    <div class="mapeo-col">
                      <v-autocomplete
                        v-model="r.account_practicada_id"
                        :items="accountsPasivo"
                        item-text="label"
                        item-value="id"
                        label="Practicada (Pasivo)"
                        dense
                        hide-details
                        clearable
                        :loading="r.saving"
                        :disabled="!update_access || r.saving"
                        @change="onChangeRegimen(r)"
                      ></v-autocomplete>
                    </div>
                    <div class="mapeo-col">
                      <v-autocomplete
                        v-model="r.account_sufrida_id"
                        :items="accountsActivo"
                        item-text="label"
                        item-value="id"
                        label="Sufrida (Activo)"
                        dense
                        hide-details
                        clearable
                        :loading="r.saving"
                        :disabled="!update_access || r.saving"
                        @change="onChangeRegimen(r)"
                      ></v-autocomplete>
                    </div>
                  </div>
                  <div class="mapeo-row__status">
                    <v-icon v-if="r.account_practicada_id || r.account_sufrida_id" color="green">check_circle</v-icon>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" color="grey lighten-1">radio_button_unchecked</v-icon>
                      </template>
                      <span>Sin mapear: se imputa a la cuenta de sistema genérica.</span>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </template>
    </v-container>

    <DialogPuestaEnMarcha
      :open="puestaEnMarchaDialog"
      :emitter="emitter_selected"
      :accounts="accounts"
      @accept="onPuestaEnMarcha"
      @cancel="puestaEnMarchaDialog = false"
    />

    <DialogAutoMacheo
      :open="autoMacheoDialog"
      :suggestions="autoMacheoSuggestions"
      :applying="applying_auto_macheo"
      @accept="onAplicarAutoMacheo"
      @cancel="autoMacheoDialog = false"
    />

    <DialogYesNo
      :open="confirmDialog"
      title="Contabilidad habilitada"
      message="La contabilidad de este emisor está habilitada. Cambiar esta cuenta afectará los próximos asientos. ¿Confirmás el cambio?"
      nameAccept="Sí, cambiar"
      nameCancel="Cancelar"
      :maxWidth="460"
      @accept="confirmSave"
      @cancel="cancelSave"
    />
  </div>
</template>

<script>
import { emmiterService } from "@/libs/ws/emmiterService";
import { contabilidadService } from "@/libs/ws/contabilidadService";
import Constant from "@/constants/sections";
import RolMixin from "@/mixins/RolMixin.vue";
import ExportMixin from "@/mixins/ExportMixin.vue";
import Title from "@/components/Title.vue";
import DialogYesNo from "@/components/DialogYesNo.vue";
import DialogPuestaEnMarcha from "./DialogPuestaEnMarcha.vue";
import DialogAutoMacheo from "./DialogAutoMacheo.vue";

const DEFAULT_META = {
  DEUDORES_VENTAS: {
    name: "Deudores por Ventas",
    hint: "Activo",
    tip: "Deuda de los clientes por ventas a crédito. El motor la debita al emitir cada factura de venta.",
  },
  ACREEDORES_COMPRAS: {
    name: "Acreedores por Compras",
    hint: "Pasivo",
    tip: "Deuda con proveedores por compras a crédito. El motor la acredita al registrar cada comprobante de compra/gasto.",
  },
  IVA_DEBITO: {
    name: "IVA Débito Fiscal",
    hint: "Pasivo",
    tip: "IVA que generás en tus ventas fiscales (A/B/M). Se acredita como pasivo en cada factura.",
  },
  IVA_CREDITO: {
    name: "IVA Crédito Fiscal",
    hint: "Activo",
    tip: "IVA que pagás en tus compras fiscales. Se debita como crédito fiscal en cada gasto.",
  },
  VENTAS: {
    name: "Ventas",
    hint: "Ingresos",
    tip: "Cuenta de ingreso por defecto para facturas fiscales, cuando el concepto de la línea no tiene su propia cuenta mapeada.",
  },
  VENTAS_X: {
    name: "Ventas X (no fiscal)",
    hint: "Ingresos",
    tip: "Cuenta de ingreso por defecto para comprobantes X (no fiscales). Acá el IVA no se discrimina: se suma al ingreso.",
  },
  COMPRAS_GASTOS: {
    name: "Compras / Gastos",
    hint: "Egresos",
    tip: "Cuenta de egreso por defecto para compras/gastos, cuando el concepto de la línea no tiene su propia cuenta mapeada.",
  },
  VALORES_RECHAZADOS_TERCEROS: {
    name: "Valores Rechazados (terceros)",
    hint: "Activo",
    tip: "Cuenta puente para cheques de terceros que recibiste y fueron rechazados; es la misma cuenta de Tesorería. En la ND de compensación por rechazo cancela el valor caído y reinstala la deuda del cliente en Deudores por Ventas.",
  },
  VALORES_RECHAZADOS_PROPIOS: {
    name: "Valores Rechazados (propios)",
    hint: "Pasivo",
    tip: "Cuenta puente para cheques propios que emitiste y fueron rechazados; es la misma cuenta de Tesorería. En la ND de compensación por rechazo reinstala tu deuda con el proveedor en Acreedores por Compras.",
  },
  RETENCION_SUFRIDA: {
    name: "Retención Sufrida",
    hint: "Activo",
    tip: "Retenciones que te practicaron al cobrar. Son un crédito fiscal (activo) a computar.",
  },
  RETENCION_PRACTICADA: {
    name: "Retención Practicada",
    hint: "Pasivo",
    tip: "Retenciones que practicaste al pagar. Son un pasivo a depositar ante el fisco.",
  },
  PERCEPCION: {
    name: "Percepción (practicada)",
    hint: "Pasivo",
    tip: "Percepciones que practicás en una venta. Son un pasivo a depositar ante el fisco.",
  },
  PERCEPCION_SUFRIDA: {
    name: "Percepción Sufrida",
    hint: "Activo",
    tip: "Percepciones que te practicaron en una compra. Son un crédito fiscal (activo) a computar.",
  },
  RESULTADO_EJERCICIO: {
    name: "Resultado del Ejercicio",
    hint: "Patrimonio Neto",
    tip: "Cuenta de Patrimonio Neto donde se refunde el resultado al cerrar el ejercicio (asiento de cierre).",
  },
  RESULTADOS_ACUMULADOS: {
    name: "Resultados Acumulados",
    hint: "Patrimonio Neto",
    tip: "Contrapartida del asiento de apertura: absorbe todo lo que la empresa arrastra de antes de la fecha de corte contable (resultados de ejercicios anteriores, aportes, ajustes).",
  },
};

export default {
  components: { Title, DialogYesNo, DialogPuestaEnMarcha, DialogAutoMacheo },
  mixins: [RolMixin, ExportMixin],
  data() {
    return {
      page_title: "Configuración Contable",
      emitters: [],
      emitter_selected: null,
      loading_emitters: false,
      mapeoData: null,
      loading_mapeo: false,
      loading_action: false,
      tab: 0,
      searchMoney: "",
      searchConcepto: "",
      searchRegimen: "",
      confirmDialog: false,
      puestaEnMarchaDialog: false,
      pendingSave: null,
      loading_sugerencias: false,
      applying_auto_macheo: false,
      autoMacheoDialog: false,
      autoMacheoSuggestions: null,
    };
  },
  computed: {
    accounts() {
      return this.mapeoData ? this.mapeoData.accounts : [];
    },
    accountsResultado() {
      return this.accounts.filter((a) => a.is_resultado);
    },
    accountsPatrimonial() {
      return this.accounts.filter((a) => !a.is_resultado);
    },
    accountsIngresos() {
      return this.accounts.filter((a) => a.grupo === 4);
    },
    accountsEgresos() {
      return this.accounts.filter((a) => a.grupo === 5);
    },
    accountsActivo() {
      return this.accounts.filter((a) => a.grupo === 1);
    },
    accountsPasivo() {
      return this.accounts.filter((a) => a.grupo === 2);
    },
    regimenes() {
      return this.mapeoData ? this.mapeoData.regimenes : [];
    },
    filteredRegimenes() {
      const s = (this.searchRegimen || "").toLowerCase();
      if (!s) return this.regimenes;
      return this.regimenes.filter(
        (r) =>
          (r.tributo_name || "").toLowerCase().includes(s) ||
          (r.impuesto_name || "").toLowerCase().includes(s) ||
          (r.jurisdiccion ? r.jurisdiccion.name : "").toLowerCase().includes(s)
      );
    },
    regimenesMapeados() {
      return this.regimenes.filter((r) => r.account_practicada_id || r.account_sufrida_id).length;
    },
    filteredMoney() {
      const s = (this.searchMoney || "").toLowerCase();
      if (!s) return this.mapeoData.money_accounts;
      return this.mapeoData.money_accounts.filter(
        (m) => (m.name || "").toLowerCase().includes(s) || (m.code || "").toLowerCase().includes(s)
      );
    },
    filteredConceptos() {
      const s = (this.searchConcepto || "").toLowerCase();
      if (!s) return this.mapeoData.conceptos;
      return this.mapeoData.conceptos.filter((c) => (c.name || "").toLowerCase().includes(s));
    },
    totalItems() {
      return this.total("money_accounts") + this.total("conceptos") + this.total("defaults");
    },
    totalMapeadas() {
      return this.mapeadas("money_accounts") + this.mapeadas("conceptos") + this.mapeadas("defaults");
    },
    progressPercent() {
      return this.totalItems ? Math.round((this.totalMapeadas / this.totalItems) * 100) : 0;
    },
    isComplete() {
      return (
        this.groupComplete("money_accounts") &&
        this.groupComplete("conceptos") &&
        this.groupComplete("defaults")
      );
    },
  },
  mounted() {
    this.loadEmitters();
  },
  methods: {
    loadEmitters() {
      this.loading_emitters = true;
      emmiterService
        .filter({ enabled: true })
        .then((response) => {
          this.emitters = response.result || [];
          if (this.emitters.length > 0) {
            this.emitter_selected = this.emitters[0];
            this.loadMapeo();
          }
        })
        .finally(() => {
          this.loading_emitters = false;
        });
    },
    loadMapeo() {
      if (!this.emitter_selected) {
        this.mapeoData = null;
        return;
      }
      this.loading_mapeo = true;
      contabilidadService
        .mapeo({ emitter_id: this.emitter_selected.id })
        .then((response) => {
          if (response.status) this.mapeoData = this.normalize(response.result);
          else this.$toast.error(response.msg, "Error");
        })
        .catch((e) => {
          this.$toast.error(e.msg || "Error al obtener el mapeo", "Error");
        })
        .finally(() => {
          this.loading_mapeo = false;
        });
    },
    // Agrega campos reactivos de edición inline (*_id, saving) a cada fila.
    normalize(data) {
      data.money_accounts = (data.money_accounts || []).map((m) => ({
        ...m,
        account_id: m.account ? m.account.id : null,
        saving: false,
      }));
      data.conceptos = (data.conceptos || []).map((c) => ({
        ...c,
        account_venta_id: c.account_venta ? c.account_venta.id : null,
        account_compra_id: c.account_compra ? c.account_compra.id : null,
        saving: false,
      }));
      data.defaults = (data.defaults || []).map((d) => ({
        ...d,
        account_id: d.account ? d.account.id : null,
        saving: false,
      }));
      data.regimenes = (data.regimenes || []).map((r) => ({
        ...r,
        account_practicada_id: r.account_practicada ? r.account_practicada.id : null,
        account_sufrida_id: r.account_sufrida ? r.account_sufrida.id : null,
        saving: false,
      }));
      return data;
    },
    regimenKey(r) {
      return r.tributo_id + ":" + (r.jurisdiccion ? r.jurisdiccion.id : "-");
    },
    findAccount(id) {
      return this.accounts.find((a) => a.id === id) || null;
    },
    // Concepto tipo: I = solo venta, E = solo compra, A = ambos.
    showVenta(c) {
      return c.tipo === "I" || c.tipo === "A";
    },
    showCompra(c) {
      return c.tipo === "E" || c.tipo === "A";
    },
    isMoneyCoherent(m) {
      if (!m.account_id) return true;
      const acc = this.findAccount(m.account_id);
      return acc ? !!acc.is_resultado === !!m.is_resultado : true;
    },
    // --- Contadores por grupo ---
    total(group) {
      return this.mapeoData ? this.mapeoData[group].length : 0;
    },
    mapeadas(group) {
      if (!this.mapeoData) return 0;
      if (group === "money_accounts")
        return this.mapeoData.money_accounts.filter((m) => m.account_id && this.isMoneyCoherent(m)).length;
      if (group === "conceptos")
        return this.mapeoData.conceptos.filter((c) => c.account_venta_id || c.account_compra_id).length;
      return this.mapeoData.defaults.filter((d) => d.account_id).length;
    },
    pendientes(group) {
      return this.total(group) - this.mapeadas(group);
    },
    groupComplete(group) {
      return this.total(group) > 0 ? this.pendientes(group) === 0 : true;
    },
    // --- Interceptores de edición (confirmación si la contabilidad está habilitada) ---
    onChangeMoney(m) {
      if (!this.mapeoData.contabilidad_habilitada) return this.saveMoneyAccount(m);
      this.requestConfirm(
        () => this.saveMoneyAccount(m),
        () => {
          m.account_id = m.account ? m.account.id : null;
        }
      );
    },
    onChangeConcepto(c) {
      if (!this.mapeoData.contabilidad_habilitada) return this.saveConcepto(c);
      this.requestConfirm(
        () => this.saveConcepto(c),
        () => {
          c.account_venta_id = c.account_venta ? c.account_venta.id : null;
          c.account_compra_id = c.account_compra ? c.account_compra.id : null;
        }
      );
    },
    onChangeDefault(d) {
      if (!this.mapeoData.contabilidad_habilitada) return this.saveDefault(d);
      this.requestConfirm(
        () => this.saveDefault(d),
        () => {
          d.account_id = d.account ? d.account.id : null;
        }
      );
    },
    onChangeRegimen(r) {
      if (!this.mapeoData.contabilidad_habilitada) return this.saveRegimen(r);
      this.requestConfirm(
        () => this.saveRegimen(r),
        () => this.revertRegimen(r)
      );
    },
    requestConfirm(save, revert) {
      this.pendingSave = { save, revert };
      this.confirmDialog = true;
    },
    confirmSave() {
      const p = this.pendingSave;
      this.confirmDialog = false;
      this.pendingSave = null;
      if (p) p.save();
    },
    cancelSave() {
      const p = this.pendingSave;
      this.confirmDialog = false;
      this.pendingSave = null;
      if (p) p.revert();
    },
    // --- Guardado inline ---
    saveMoneyAccount(m) {
      m.saving = true;
      contabilidadService
        .mapear({
          tipo: "money_account",
          emitter_id: this.emitter_selected.id,
          money_account_id: m.id,
          account_id: m.account_id || null,
        })
        .then((response) => {
          if (response.status) {
            m.account = m.account_id ? this.findAccount(m.account_id) : null;
            this.$toast.success("Mapeo guardado.", "OK");
          } else {
            m.account_id = m.account ? m.account.id : null;
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          m.account_id = m.account ? m.account.id : null;
          this.$toast.error(e.msg || "Error al guardar", "Error");
        })
        .finally(() => {
          m.saving = false;
        });
    },
    saveConcepto(c) {
      c.saving = true;
      contabilidadService
        .mapear({
          tipo: "concepto",
          emitter_id: this.emitter_selected.id,
          concepto_id: c.id,
          account_venta_id: c.account_venta_id || null,
          account_compra_id: c.account_compra_id || null,
        })
        .then((response) => {
          if (response.status) {
            c.account_venta = c.account_venta_id ? this.findAccount(c.account_venta_id) : null;
            c.account_compra = c.account_compra_id ? this.findAccount(c.account_compra_id) : null;
            this.$toast.success("Mapeo guardado.", "OK");
          } else {
            c.account_venta_id = c.account_venta ? c.account_venta.id : null;
            c.account_compra_id = c.account_compra ? c.account_compra.id : null;
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          c.account_venta_id = c.account_venta ? c.account_venta.id : null;
          c.account_compra_id = c.account_compra ? c.account_compra.id : null;
          this.$toast.error(e.msg || "Error al guardar", "Error");
        })
        .finally(() => {
          c.saving = false;
        });
    },
    saveDefault(d) {
      d.saving = true;
      contabilidadService
        .mapear({
          tipo: "default",
          emitter_id: this.emitter_selected.id,
          key: d.key,
          account_id: d.account_id || null,
        })
        .then((response) => {
          if (response.status) {
            d.account = d.account_id ? this.findAccount(d.account_id) : null;
            this.$toast.success("Mapeo guardado.", "OK");
          } else {
            d.account_id = d.account ? d.account.id : null;
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          d.account_id = d.account ? d.account.id : null;
          this.$toast.error(e.msg || "Error al guardar", "Error");
        })
        .finally(() => {
          d.saving = false;
        });
    },
    revertRegimen(r) {
      r.account_practicada_id = r.account_practicada ? r.account_practicada.id : null;
      r.account_sufrida_id = r.account_sufrida ? r.account_sufrida.id : null;
    },
    saveRegimen(r) {
      r.saving = true;
      contabilidadService
        .mapear({
          tipo: "impuesto",
          emitter_id: this.emitter_selected.id,
          tributo_id: r.tributo_id,
          jurisdiccion_id: r.jurisdiccion ? r.jurisdiccion.id : null,
          account_practicada_id: r.account_practicada_id || null,
          account_sufrida_id: r.account_sufrida_id || null,
        })
        .then((response) => {
          if (response.status) {
            r.account_practicada = r.account_practicada_id ? this.findAccount(r.account_practicada_id) : null;
            r.account_sufrida = r.account_sufrida_id ? this.findAccount(r.account_sufrida_id) : null;
            this.$toast.success("Mapeo guardado.", "OK");
          } else {
            this.revertRegimen(r);
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          this.revertRegimen(r);
          this.$toast.error(e.msg || "Error al guardar", "Error");
        })
        .finally(() => {
          r.saving = false;
        });
    },
    // --- Habilitar / deshabilitar ---
    // Habilitar no es un toggle: hay que fijar la fecha de corte y cargar los saldos
    // iniciales, si no las cuentas patrimoniales arrancan en cero. Lo resuelve el diálogo.
    habilitar() {
      this.puestaEnMarchaDialog = true;
    },
    onPuestaEnMarcha() {
      this.puestaEnMarchaDialog = false;
      this.loadMapeo();
    },
    deshabilitar() {
      this.loading_action = true;
      contabilidadService
        .deshabilitar({ emitter_id: this.emitter_selected.id })
        .then((response) => {
          if (response.status) {
            this.$toast.success("Contabilidad deshabilitada.", "OK");
            this.loadMapeo();
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          this.$toast.error(e.msg || "Error al deshabilitar", "Error");
        })
        .finally(() => {
          this.loading_action = false;
        });
    },
    exportFile() {
      this.exportExcel(
        Constant.SECTION_CONTABILIDAD + "/exportar",
        { emitter_id: this.emitter_selected.id },
        "Configuracion-Contable-" + this.emitter_selected.name + ".xlsx"
      );
    },
    // --- Macheo automático ---
    abrirAutoMacheo() {
      if (!this.emitter_selected) return;
      this.loading_sugerencias = true;
      contabilidadService
        .sugerirMapeo({ emitter_id: this.emitter_selected.id })
        .then((response) => {
          if (response.status) {
            this.autoMacheoSuggestions = response.result;
            this.autoMacheoDialog = true;
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          this.$toast.error(e.msg || "Error al buscar coincidencias", "Error");
        })
        .finally(() => {
          this.loading_sugerencias = false;
        });
    },
    onAplicarAutoMacheo(selection) {
      this.applying_auto_macheo = true;
      const emitter_id = this.emitter_selected.id;

      const calls = [
        ...selection.money_accounts.map((m) =>
          contabilidadService.mapear({
            tipo: "money_account",
            emitter_id,
            money_account_id: m.id,
            account_id: m.account.id,
          })
        ),
        ...selection.conceptos.map((c) =>
          contabilidadService.mapear({
            tipo: "concepto",
            emitter_id,
            concepto_id: c.id,
            account_venta_id: c.account_venta_id,
            account_compra_id: c.account_compra_id,
          })
        ),
        ...selection.defaults.map((d) =>
          contabilidadService.mapear({
            tipo: "default",
            emitter_id,
            key: d.key,
            account_id: d.account.id,
          })
        ),
      ];

      Promise.allSettled(calls)
        .then((results) => {
          const ok = results.filter((r) => r.status === "fulfilled" && r.value && r.value.status).length;
          const fail = results.length - ok;
          if (fail === 0) this.$toast.success(`${ok} mapeo(s) aplicado(s).`, "OK");
          else this.$toast.warning(`${ok} mapeo(s) aplicado(s), ${fail} con error.`, "Atención");
          this.autoMacheoDialog = false;
          this.loadMapeo();
        })
        .finally(() => {
          this.applying_auto_macheo = false;
        });
    },
    defaultName(key) {
      return DEFAULT_META[key] ? DEFAULT_META[key].name : key;
    },
    defaultHint(key) {
      return DEFAULT_META[key] ? DEFAULT_META[key].hint : "—";
    },
    defaultTip(key) {
      return DEFAULT_META[key] ? DEFAULT_META[key].tip : "";
    },
  },
};
</script>

<style scoped>
.mapeo-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 4px;
  border-bottom: 1px solid #eee;
}
.mapeo-row:last-child {
  border-bottom: none;
}
.mapeo-row__info {
  flex: 1 1 0;
  min-width: 0;
}
.mapeo-row__select {
  flex: 1 1 0;
  min-width: 0;
}
/* Filas de doble selector (Conceptos, Ret. y percepciones): 40% nombre / 60% cuentas.
   El status y los gaps salen del 60%, que es la parte flexible. */
.mapeo-row--doble .mapeo-row__info {
  flex: 0 0 40%;
}
.mapeo-row__select-double {
  flex: 1 1 auto;
  display: flex;
  gap: 12px;
  min-width: 0;
}
.mapeo-col {
  flex: 1 1 0;
  min-width: 0;
}
.mapeo-na {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  color: #bdbdbd;
  font-size: 13px;
  font-style: italic;
  border-bottom: 1px dotted #e0e0e0;
}
.mapeo-row__status {
  flex: 0 0 32px;
  text-align: center;
}
@media (max-width: 700px) {
  .mapeo-row {
    flex-wrap: wrap;
  }
  .mapeo-row__info,
  .mapeo-row--doble .mapeo-row__info {
    flex: 1 1 100%;
  }
  .mapeo-row__select-double {
    flex-wrap: wrap;
  }
}
</style>
